/**
 * Дифф пакета: «было → стало», сгруппированное так же, как на ревью (§6.3, §8).
 *
 * Порядок групп один на весь проект — новые бренды, бренды по алфавиту,
 * массовые правки, архивация и отвязки, справочники. Он совпадает с порядком
 * сообщения в Telegram: человек видит один и тот же текст в чате, на ревью и в
 * журнале, и ему не приходится сопоставлять две разные картины мира.
 *
 * Массовая операция (`operationId`) сворачивается в одну строку: пакет из 230
 * правок «Приоритет SEO → TOP-3» не должен занимать два экрана прокрутки.
 */
import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { Change, ChannelCode } from '@shared/types.ts'
import { IconChevronDown, IconChevronRight } from '../../components/index.js'
import { useApp } from '../../store/app-store.js'
import { countOf, forms } from '../../texts.js'
import {
  changeAddress, changeRowLabel, isStandaloneChange, valueLabel,
} from './texts.history.js'
import './ChangeDiff.css'

export interface ChangeDiffProps {
  changes: Change[]
  /** Подсветка вхождений поиска по бренду (§8): фон `--fresh-bg`. */
  highlight?: string
  /** Канал для ссылки в таблицу: без него ссылка ведёт в канал по умолчанию. */
  channel?: ChannelCode | null
  /** Клик по правке ведёт в таблицу на эту ячейку (§8). В сводке — тоже. */
  linkToTable?: boolean
}

interface OperationRow {
  kind: 'operation'
  id: string
  label: string
  changes: Change[]
}

interface SingleRow {
  kind: 'single'
  change: Change
}

type DiffRow = OperationRow | SingleRow

interface DiffGroup {
  id: string
  /** null — группа без заголовка (одиночный пакет из одной правки) */
  title: string | null
  marker: 'new' | 'bulk' | 'archive' | 'admin' | null
  count: number
  rows: DiffRow[]
}

const ARCHIVE_ACTIONS = new Set(['archive', 'restore', 'detach'])
const ADMIN_ENTITIES = new Set(['category', 'enum_value', 'field_definition', 'user', 'role', 'setting', 'change_set'])

const collator = new Intl.Collator('ru', { sensitivity: 'base', numeric: true })

/** Название бренда: из среза, а если бренда уже нет — из подписи правки. */
function brandNameOf(change: Change, names: Map<number, string>): string {
  if (change.brandId != null) {
    const known = names.get(change.brandId)
    if (known) return known
  }
  return change.entityLabel.split(' · ')[0] ?? '—'
}

/** Подпись свёрнутой массовой операции: «Приоритет SEO → TOP-3 · 12 брендов». */
function operationLabel(changes: Change[]): string {
  const first = changes[0]
  if (!first) return 'Массовая правка'
  const field = changeRowLabel(first)
  const brands = new Set(changes.map((c) => c.brandId).filter((id): id is number => id != null)).size
  const target = isStandaloneChange(first)
    ? field
    : `${field} → ${valueLabel(first.newLabel, first.newValue)}`
  return brands > 0 ? `${target} · ${countOf(brands, forms.brand)}` : target
}

function buildGroups(changes: Change[], names: Map<number, string>): DiffGroup[] {
  // Массовые операции собираем первыми: их правки не должны попасть в группы брендов.
  const byOperation = new Map<string, Change[]>()
  for (const change of changes) {
    if (!change.operationId) continue
    const list = byOperation.get(change.operationId) ?? []
    list.push(change)
    byOperation.set(change.operationId, list)
  }
  // Операция из одной правки — это обычная правка, сворачивать нечего.
  for (const [id, list] of byOperation) if (list.length < 2) byOperation.delete(id)

  const plain = changes.filter((c) => !c.operationId || !byOperation.has(c.operationId))

  const newBrandIds = new Set(
    plain
      .filter((c) => c.action === 'create' && c.entityType === 'brand')
      .map((c) => c.brandId)
      .filter((id): id is number => id != null),
  )

  const newBrands: Change[] = []
  const archive: Change[] = []
  const admin: Change[] = []
  const byBrand = new Map<number, Change[]>()
  const orphans: Change[] = []

  for (const change of plain) {
    if (ADMIN_ENTITIES.has(change.entityType)) { admin.push(change); continue }
    if (change.brandId != null && newBrandIds.has(change.brandId)) { newBrands.push(change); continue }
    if (ARCHIVE_ACTIONS.has(change.action)) { archive.push(change); continue }
    if (change.brandId == null) { orphans.push(change); continue }
    const list = byBrand.get(change.brandId) ?? []
    list.push(change)
    byBrand.set(change.brandId, list)
  }

  const groups: DiffGroup[] = []

  if (newBrands.length > 0) {
    const brands = new Set(newBrands.map((c) => c.brandId).filter((id): id is number => id != null))
    groups.push({
      id: 'new',
      title: `Новые бренды (${brands.size})`,
      marker: 'new',
      count: newBrands.length,
      rows: newBrands.map((change) => ({ kind: 'single', change })),
    })
  }

  const brandGroups = [...byBrand.entries()]
    .flatMap(([brandId, list]) => {
      const first = list[0]
      if (!first) return []
      return [{
        brandId,
        name: brandNameOf(first, names),
        list: [...list].sort((a, b) => a.seq - b.seq),
      }]
    })
    .sort((a, b) => collator.compare(a.name, b.name))

  for (const group of brandGroups) {
    groups.push({
      id: `brand-${group.brandId}`,
      title: group.name,
      marker: null,
      count: group.list.length,
      rows: group.list.map((change) => ({ kind: 'single', change })),
    })
  }

  if (byOperation.size > 0) {
    const rows: DiffRow[] = [...byOperation.entries()].map(([id, list]) => ({
      kind: 'operation',
      id,
      label: operationLabel([...list].sort((a, b) => a.seq - b.seq)),
      changes: [...list].sort((a, b) => a.seq - b.seq),
    }))
    const total = [...byOperation.values()].reduce((sum, list) => sum + list.length, 0)
    groups.push({ id: 'bulk', title: 'Массовая правка', marker: 'bulk', count: total, rows })
  }

  if (archive.length > 0) {
    groups.push({
      id: 'archive',
      title: 'Архивация и отвязки',
      marker: 'archive',
      count: archive.length,
      rows: archive.map((change) => ({ kind: 'single', change })),
    })
  }

  if (admin.length > 0 || orphans.length > 0) {
    const list = [...admin, ...orphans].sort((a, b) => a.seq - b.seq)
    groups.push({
      id: 'admin',
      title: 'Справочники и настройки',
      marker: 'admin',
      count: list.length,
      rows: list.map((change) => ({ kind: 'single', change })),
    })
  }

  return groups
}

/** Подсветка вхождений поиска фоном `--fresh-bg` (§8). */
function Highlight({ text, query }: { text: string; query?: string }) {
  const needle = (query ?? '').trim()
  if (needle.length === 0) return <>{text}</>
  const lower = text.toLowerCase()
  const target = needle.toLowerCase()
  const parts: ReactNode[] = []
  let from = 0
  let hit = lower.indexOf(target)
  let key = 0
  while (hit >= 0) {
    if (hit > from) parts.push(text.slice(from, hit))
    parts.push(<mark className="cdiff__hl" key={key++}>{text.slice(hit, hit + needle.length)}</mark>)
    from = hit + needle.length
    hit = lower.indexOf(target, from)
  }
  if (from < text.length) parts.push(text.slice(from))
  return <>{parts}</>
}

/** Ссылка в таблицу на эту ячейку (§8: курсор в ячейке и подсветка 600 ms). */
function tableHref(change: Change, channel: ChannelCode | null | undefined): string | null {
  if (change.brandId == null) return null
  const params = new URLSearchParams()
  const target = change.key.channelCode ?? channel
  if (target) params.set('ch', target)
  params.set('sel', String(change.brandId))
  if (change.fieldCode) params.set('cell', change.fieldCode)
  params.set('flash', '1')
  return `/brands?${params.toString()}`
}

function ValuePair({ change, highlight }: { change: Change; highlight?: string }) {
  if (isStandaloneChange(change)) {
    const single = valueLabel(change.oldLabel ?? change.newLabel, change.oldValue ?? change.newValue)
    if (single === 'пусто') return null
    return <span className="cdiff__new"><Highlight text={single} query={highlight} /></span>
  }
  return (
    <>
      <span className="cdiff__old">{valueLabel(change.oldLabel, change.oldValue)}</span>
      <span className="cdiff__arrow" aria-hidden="true">→</span>
      <span className="cdiff__new"><Highlight text={valueLabel(change.newLabel, change.newValue)} query={highlight} /></span>
    </>
  )
}

function ChangeLine({
  change, highlight, channel, linkToTable, showBrand,
}: {
  change: Change
  highlight?: string
  channel?: ChannelCode | null
  linkToTable?: boolean
  showBrand?: boolean
}) {
  const names = useApp((s) => s.brandsById)
  const brandName = change.brandId != null ? names.get(change.brandId)?.name ?? null : null
  const address = changeAddress(change, brandName)
  const href = linkToTable ? tableHref(change, channel) : null

  const body = (
    <>
      <span className="cdiff__field">
        {showBrand ? (
          <span className="cdiff__brand">
            <Highlight text={brandName ?? change.entityLabel.split(' · ')[0] ?? '—'} query={highlight} />
            {' · '}
          </span>
        ) : null}
        <Highlight text={changeRowLabel(change)} query={highlight} />
        {address ? <span className="cdiff__addr"> · <Highlight text={address} query={highlight} /></span> : null}
      </span>
      <span className="cdiff__values">
        <ValuePair change={change} highlight={highlight} />
      </span>
    </>
  )

  if (href) {
    return <Link className="cdiff__row cdiff__row--link" to={href}>{body}</Link>
  }
  return <span className="cdiff__row">{body}</span>
}

function OperationLine({
  row, highlight, channel, linkToTable,
}: {
  row: OperationRow
  highlight?: string
  channel?: ChannelCode | null
  linkToTable?: boolean
}) {
  const [open, setOpen] = useState(false)
  return (
    <li className="cdiff__item">
      <button
        type="button"
        className="cdiff__op"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="cdiff__op-icon" aria-hidden="true">
          {open ? <IconChevronDown /> : <IconChevronRight />}
        </span>
        <span className="cdiff__field">
          <Highlight text={row.label} query={highlight} />
        </span>
        <span className="cdiff__values cdiff__values--muted">{countOf(row.changes.length, forms.change)}</span>
      </button>
      {open ? (
        <ul className="cdiff__sub">
          {row.changes.map((change) => (
            <li className="cdiff__item" key={change.id}>
              <ChangeLine
                change={change}
                highlight={highlight}
                channel={channel}
                linkToTable={linkToTable}
                showBrand
              />
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  )
}

export function ChangeDiff({ changes, highlight, channel, linkToTable = true }: ChangeDiffProps) {
  const names = useApp((s) => s.brandsById)
  const nameMap = useMemo(() => {
    const map = new Map<number, string>()
    for (const [id, brand] of names) map.set(id, brand.name)
    return map
  }, [names])

  const groups = useMemo(() => buildGroups(changes, nameMap), [changes, nameMap])

  if (changes.length === 0) {
    return <p className="cdiff__empty t-cell">В пакете нет правок</p>
  }

  return (
    <div className="cdiff">
      {groups.map((group) => (
        <section className="cdiff__group" key={group.id} aria-label={group.title ?? undefined}>
          {group.title ? (
            <header className="cdiff__head">
              <h3 className={group.marker === 'new' ? 'cdiff__title cdiff__title--new' : 'cdiff__title'}>
                {group.title}
              </h3>
              <span className="cdiff__count t-meta">{countOf(group.count, forms.change)}</span>
            </header>
          ) : null}
          <ul className="cdiff__list">
            {group.rows.map((row) => (
              row.kind === 'operation' ? (
                <OperationLine
                  key={row.id}
                  row={row}
                  highlight={highlight}
                  channel={channel}
                  linkToTable={linkToTable}
                />
              ) : (
                <li className="cdiff__item" key={row.change.id}>
                  <ChangeLine
                    change={row.change}
                    highlight={highlight}
                    channel={channel}
                    linkToTable={linkToTable}
                    showBrand={group.marker === 'archive' || group.marker === 'new'}
                  />
                </li>
              )
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
